import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  fetchHikvisionDevices,
  fetchControllerHistoryAndWait,
  syncControllerEventsToDb,
  type HistorySyncSummary,
} from "../../../store/oasis/hikvisionAccessStore";
import type { TruckYardOption } from "../../../utils/truckYardOptions";

// Shared controller-log sync used by both the Access events page and the
// Inspections history page: pull each yard's controller history and persist the
// records the live pipeline missed (idempotent server-side). Extracted so both
// screens offer the same "Sync" button + modal without duplicating the loop.

export interface SyncThemeClasses {
  cardBg: string;
  text: string;
  textSecondary: string;
  buttonHover: string;
}

export interface SyncFormData {
  yard_id: string; // "" → all yards
  allHistory: boolean;
  from: string;
  to: string;
}

export interface YardSyncResult {
  totals: HistorySyncSummary;
  skips: {
    notAccepted: number;
    notDecision: number;
    noIdentity: number;
    duplicate: number;
    noDevice: number;
  };
  failures: string[];
}

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultSyncRange(): { from: string; to: string } {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return { from: toDateInput(yesterday), to: toDateInput(now) };
}

/**
 * Run the controller-log sync across one or all yards, accumulating a combined
 * summary. Pure data flow — the caller owns the surrounding UI (spinners,
 * toasts, list refresh).
 */
export async function runYardSync(
  yards: TruckYardOption[],
  data: SyncFormData,
  t: TFunction,
): Promise<YardSyncResult> {
  const targetYards = data.yard_id
    ? yards.filter((y) => y.id === data.yard_id)
    : yards;

  const totals: HistorySyncSummary = {
    created: 0, skipped: 0, entries: 0, exits: 0, inspections: 0, total: 0,
  };
  const skips = { notAccepted: 0, notDecision: 0, noIdentity: 0, duplicate: 0, noDevice: 0 };
  const failures: string[] = [];

  if (targetYards.length === 0) return { totals, skips, failures };

  const from = data.allHistory || !data.from ? null : new Date(data.from).toISOString();
  const to = data.allHistory || !data.to ? null : new Date(`${data.to}T23:59:59`).toISOString();

  for (const y of targetYards) {
    const { data: devices } = await fetchHikvisionDevices({ yard_id: y.id, limit: 1, page: 1 });
    const device = devices[0];
    if (!device) {
      failures.push(`${y.name}: ${t("access.sync.noDevice")}`);
      continue;
    }
    const history = await fetchControllerHistoryAndWait({
      device_id: device.id,
      from,
      to,
      max: 2000,
      filter: "access",
    });
    if (history.status !== "completed") {
      failures.push(`${y.name}: ${history.error || t("access.sync.failed")}`);
      continue;
    }
    // The on-site bridge answered with a different controller than this device
    // (single-bridge site): skip so its log isn't re-stamped onto this yard as
    // duplicates of the controller that really owns it.
    if (
      history.controllerSerial &&
      device.device_serial &&
      history.controllerSerial !== device.device_serial
    ) {
      failures.push(`${y.name}: ${t("access.sync.serialMismatch")}`);
      continue;
    }
    const s = await syncControllerEventsToDb(device.id, history.events);
    totals.created += s.created;
    totals.skipped += s.skipped;
    totals.entries += s.entries;
    totals.exits += s.exits;
    totals.inspections += s.inspections;
    totals.total += s.total;
    if (s.skippedReasons) {
      skips.notAccepted += s.skippedReasons.notAccepted;
      skips.notDecision += s.skippedReasons.notDecision;
      skips.noIdentity += s.skippedReasons.noIdentity;
      skips.duplicate += s.skippedReasons.duplicate;
      skips.noDevice += s.skippedReasons.noDevice;
    }
  }

  return { totals, skips, failures };
}

export function SyncModal({
  themeClasses,
  isDarkMode,
  yards,
  defaultYardId,
  lockedYardId,
  onClose,
  onSubmit,
  submitting,
}: {
  themeClasses: SyncThemeClasses;
  isDarkMode: boolean;
  yards: TruckYardOption[];
  defaultYardId: string;
  lockedYardId?: string;
  onClose: () => void;
  onSubmit: (data: SyncFormData) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const range = defaultSyncRange();
  const [form, setForm] = useState<SyncFormData>({
    yard_id: lockedYardId || defaultYardId || "",
    allHistory: false,
    from: range.from,
    to: range.to,
  });

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const inputCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={submitting ? undefined : onClose} />
      <div className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>{t("access.sync.title")}</h2>
          <button onClick={onClose} disabled={submitting} className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors disabled:opacity-50`}>
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <p className={`text-sm ${themeClasses.textSecondary}`}>{t("access.sync.subtitle")}</p>

          <div>
            <label className={labelCls}>{t("access.sync.yard")}</label>
            <select
              value={form.yard_id}
              onChange={(e) => setForm((p) => ({ ...p, yard_id: e.target.value }))}
              disabled={!!lockedYardId}
              className={`${inputCls} ${lockedYardId ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {!lockedYardId && <option value="">{t("access.sync.allYards")}</option>}
              {yards.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.allHistory}
              onChange={(e) => setForm((p) => ({ ...p, allHistory: e.target.checked }))}
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>{t("access.sync.allHistory")}</span>
          </label>

          {!form.allHistory && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{t("access.sync.from")}</label>
                <input
                  type="date"
                  value={form.from}
                  onChange={(e) => setForm((p) => ({ ...p, from: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>{t("access.sync.to")}</label>
                <input
                  type="date"
                  value={form.to}
                  onChange={(e) => setForm((p) => ({ ...p, to: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
          )}

          <p className={`text-[11px] ${themeClasses.textSecondary} inline-flex items-start gap-1`}>
            <i className="ri-time-line mt-px"></i>
            <span>{t("access.sync.slowHint")}</span>
          </p>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={submitting}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium ${surface} ${themeClasses.text} disabled:opacity-50`}>
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2.5 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-gray-900 disabled:opacity-60 flex items-center">
              {submitting
                ? <><i className="ri-loader-4-line animate-spin mr-1"></i>{t("access.sync.submitting")}</>
                : <><i className="ri-refresh-line mr-1"></i>{t("access.sync.submit")}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
